import { routeHref } from '../../lib/router.js'
import { touched } from '../../lib/live.js'
import { wallDate } from '../../lib/time.js'
import { COUNTDOWN_DAYS, addDays, calendarItems, calendarPrefs, dayLabel, eventsRequest, nextUp, nextUpCalendars, nextUpCountdowns, nextUpDays, startsIn, subscriptionsRequest, timeLabel, today, viewWindow } from '../../lib/calendar.js'
import { checkHabit, habitChips } from '../../lib/habits.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-next-up ---
let __fez_component_0;
{
const OPEN = 'next-up-open'

// Outside the instance: hiding unmounts it before the toast's Undo runs.
async function showNextUp(on) {
  Fez.state.set('me', await window.App.api('user/update', { next_up: on }, { silent: true }))
}
__fez_component_0 = class {
  init() {
    this.state.loading = true
    this.state.error = ''
    this.state.raw = []
    this.state.countRaw = []
    this.state.subscriptions = []
    this.state.habits = []
    this.state.seq = 0
    this.state.day = today()
    this.state.minute = Date.now()
    this.state.open = Fez.localStorage.get(OPEN, !window.matchMedia('(max-width: 767px)').matches)
  }

  onMount() {
    this.on(window, 'focus', () => this.load(), { throttle: 2000 })
    this.subscribe('live:change', (change) => {
      if (touched(change, this.containers()).length) this.load()
    })
    this.subscribe('live:calendar', () => this.load())
    this.setInterval(() => {
      this.state.minute = Date.now()
      if (today() !== this.state.day) this.load()
    }, 30000)
    this.load()
  }

  onGlobalStateChange(key) {
    if (key === 'mailboxes') this.load()
  }

  calendars() {
    const hide = String(calendarPrefs().hide || '').split(',').filter(Boolean)
    return nextUpCalendars(this.globalState.mailboxes, this.state.subscriptions, hide, this.globalState.mailboxSession ? '' : this.globalState.me?.ref)
  }

  containers() {
    return [...new Set(this.calendars().map((calendar) => calendar.container).filter(Boolean))]
  }

  async load() {
    const seq = ++this.state.seq
    const day = today()
    const session = this.globalState.mailboxSession
    const events = eventsRequest(viewWindow(nextUpDays(day)))
    const counting = eventsRequest(viewWindow([day, addDays(day, COUNTDOWN_DAYS)]), 'countdown')
    const subscriptions = subscriptionsRequest(session)
    const person = Boolean(this.globalState.me) && !session
    try {
      const [result, counted, subscribed, habits] = await Promise.all([
        window.App.api(events.path, events.params, { silent: true }),
        window.App.api(counting.path, counting.params, { silent: true }),
        window.App.api(subscriptions.path, subscriptions.params, { silent: true }),
        person ? window.App.api('planner/habits', { from: day, to: day }, { silent: true }) : { items: [] },
      ])
      if (seq !== this.state.seq) return
      this.state.raw = result.items
      this.state.countRaw = counted.items
      this.state.subscriptions = subscribed.items
      this.state.habits = habits.items
      this.state.day = day
      this.state.minute = Date.now()
      this.state.error = ''
    } catch (error) {
      if (seq === this.state.seq) this.state.error = error.message
    } finally {
      if (seq === this.state.seq) this.state.loading = false
    }
  }

  toggle() {
    this.state.open = !this.state.open
    Fez.localStorage.set(OPEN, this.state.open)
  }

  // A habit chip: today's mark at once, then the server's streak (or the list again).
  async checkHabit(ref, day, done) {
    this.state.habits = checkHabit(this.state.habits, ref, day, done)
    try {
      const saved = await window.App.api('planner/habit_check', { habit: ref, day, done }, { silent: true })
      this.state.habits = checkHabit(this.state.habits, ref, day, saved.done, saved.streak)
    } catch (error) {
      window.Toast?.error(error.message)
      this.load()
    }
  }

  async hide() {
    try {
      await showNextUp(false)
      window.Toast?.success('Next up is hidden; Settings shows it again.', { action: { label: 'Undo', run: () => showNextUp(true) }, ms: 8000 })
    } catch (error) {
      window.Toast?.error(error.message)
    }
  }

  // Where an event opens in the calendar: its day there and the instance.
  href(item, day) {
    return routeHref('calendar', { date: day, event: item.event, instance: String(item.instance), calendar: item.calendar })
  }

  beforeRender() {
    const now = wallDate(new Date(this.state.minute))
    const days = nextUpDays(this.state.day)
    const calendars = this.calendars()
    const session = this.globalState.mailboxSession
    const { groups, next, join } = nextUp(calendarItems(this.state.raw, calendars, session), days, now)
    this.state.groups = groups.map((group) => ({
      ...group,
      moreHref: routeHref('calendar', { view: 'day', date: group.day }),
      rows: group.rows.map((row) => ({
        ...row,
        href: this.href(row, group.day),
        join: join?.key === row.key ? row.url : '',
        soon: next?.key === row.key || join?.key === row.key ? startsIn(row, now) : '',
        tip: [row.title, row.location].filter(Boolean).join(' · '),
      })),
    }))
    const counting = calendarItems(this.state.countRaw, calendars, session)
    this.state.counts = nextUpCountdowns(counting, days).map((line) => {
      const item = counting.find((entry) => entry.key === line.key)
      return { ...line, href: this.href(item, item.firstDay) }
    })
    this.state.empty = !this.state.counts.length && groups.every((group) => !group.rows.length)
    // The folded line: the next event and how soon, else today's all-day events.
    const allDay = groups[0].rows.filter((row) => row.all_day)
    const peekDay = next && groups.find((group) => group.day === next.firstDay)
    this.state.peek = next
      ? { title: next.title, when: startsIn(next, now) || `${peekDay?.label || dayLabel(next.firstDay, { weekday: 'short' })} ${timeLabel(next.startAt)}`, join: join?.key === next.key ? next.url : '' }
      : allDay.length
        ? { title: allDay.map((row) => row.title).join(', '), when: 'All day', join: '' }
        : { title: 'Nothing in the next 3 days', when: '', join: '' }
    this.state.canHide = Boolean(this.globalState.me)
    this.state.calendarHref = routeHref('calendar')
    this.state.chips = habitChips(this.state.habits, this.state.day, this.state.day)
  }

  CSS = `:fez {
display:block;.nu{margin:var(--space-2) var(--space-3)}.nu-head{display:flex;align-items:center;gap:var(--space-1);min-height:40px;padding:0 var(--space-2) 0 0}.nu-toggle{display:flex;flex:1;align-items:center;gap:var(--space-2);min-width:0;min-height:40px;padding:0 var(--space-1) 0 var(--space-3);border:0;background:transparent;color:var(--color-text);font:inherit;text-align:left;cursor:pointer}.nu-mark{display:inline-flex;flex:none;color:var(--color-accent)}.nu-name{flex:none;font-size:var(--text-sm);font-weight:var(--weight-semibold)}.nu-peek{display:flex;flex:1;align-items:baseline;gap:var(--space-2);min-width:0;font-size:var(--text-sm)}.nu-peek-title{overflow:hidden;color:var(--color-text-muted);text-overflow:ellipsis;white-space:nowrap}.nu-peek-when{flex:none;color:var(--color-accent);font-size:var(--text-xs);font-weight:var(--weight-semibold)}.nu-grow{flex:1}.nu-caret{display:inline-flex;flex:none;color:var(--color-text-subtle)}.nu-icon{display:grid;flex:none;place-items:center;width:30px;height:30px;padding:0;border:0;border-radius:var(--radius-sm);background:transparent;color:var(--color-text-subtle);cursor:pointer;&:hover{background:var(--color-bg-hover);color:var(--color-text)}}.nu-body{padding:0 var(--space-3) var(--space-3)}.nu-habits{margin-bottom:var(--space-3)}.nu-counts{display:flex;flex-wrap:wrap;gap:var(--space-1) var(--space-3);margin-bottom:var(--space-2)}.nu-count{display:inline-flex;align-items:center;gap:var(--space-1);color:var(--color-text-muted);font-size:var(--text-xs);text-decoration:none;&:hover{color:var(--color-text)}}.nu-day{display:flex;align-items:baseline;gap:var(--space-2);margin:var(--space-2) 0 var(--space-1);font-size:var(--text-xs);&:first-child{margin-top:0}}.nu-day-name{color:var(--color-text-muted);font-weight:var(--weight-semibold);text-transform:uppercase;letter-spacing:0.04em;&.today{color:var(--color-accent)}}.nu-day-date{color:var(--color-text-subtle)}.nu-free{margin-left:auto;color:var(--color-text-subtle)}.nu-row{display:flex;align-items:center;gap:var(--space-2);border-left:3px solid var(--tag);border-radius:var(--radius-sm);background:var(--color-bg-subtle);& + .nu-row{margin-top:var(--space-1)}&:hover{background:var(--color-bg-hover)}}.nu-open{display:flex;flex:1;align-items:baseline;gap:var(--space-2);min-width:0;padding:var(--space-2);color:var(--color-text);text-decoration:none}.nu-time{flex:none;width:64px;color:var(--color-text-muted);font-size:var(--text-xs);font-variant-numeric:tabular-nums}.nu-text{display:grid;flex:1;gap:1px;min-width:0}.nu-title{overflow:hidden;font-size:var(--text-sm);font-weight:var(--weight-medium);text-overflow:ellipsis;white-space:nowrap}.nu-sub{display:flex;gap:var(--space-2);min-width:0;font-size:var(--text-xs)}.nu-soon{flex:none;color:var(--color-accent);font-weight:var(--weight-semibold)}.nu-where{overflow:hidden;color:var(--color-text-muted);text-overflow:ellipsis;white-space:nowrap}.nu-flag{display:inline-flex;flex:none;align-self:center;color:var(--color-text-subtle)}.nu-join{flex:none;padding-right:var(--space-2)}.nu-more{display:inline-block;margin-top:var(--space-1);color:var(--color-text-muted);font-size:var(--text-xs);text-decoration:none;&:hover{color:var(--color-text)}}.nu-note{display:flex;align-items:center;gap:var(--space-2);color:var(--color-text-muted);font-size:var(--text-sm)}
}`

  HTML = `<section class="nu ui-card pad-none" aria-label="Next up">
<div class="nu-head">
<button type="button" class="nu-toggle" aria-expanded={state.open ? 'true' : 'false'} title={state.open ? 'Fold Next up' : 'Show today and the next two days'} onclick="fez.toggle()">
<span class="nu-mark"><ui-icon name="calendar-dots" size="16"></ui-icon></span>
<span class="nu-name">Next up</span>
{#if state.open || state.loading}
<span class="nu-grow"></span>
{:else}
<span class="nu-peek">
<span class="nu-peek-title">{state.peek.title}</span>
{#if state.peek.when}<span class="nu-peek-when">{state.peek.when}</span>{/if}
</span>
{/if}
<span class="nu-caret">{#if state.open}<ui-icon name="caret-up" size="14"></ui-icon>{:else}<ui-icon name="caret-down" size="14"></ui-icon>{/if}</span>
</button>
{#if !state.open && state.peek.join}
<ui-button size="sm" variant="primary" icon="video-camera" label="Join" href={state.peek.join} target="_blank" rel="noreferrer noopener"></ui-button>
{/if}
<a class="nu-icon" href={state.calendarHref} title="Open calendar" aria-label="Open calendar"><ui-icon name="calendar" size="16"></ui-icon></a>
{#if state.canHide}
<button type="button" class="nu-icon" title="Hide Next up" aria-label="Hide Next up" onclick="fez.hide()"><ui-icon name="eye-slash" size="16"></ui-icon></button>
{/if}
</div>
{#if state.open}
<div class="nu-body">
{#if state.chips.length}
<div class="nu-habits"><calendar-habits :habits="state.chips" day={state.day} :on_check="(ref, day, done) => fez.checkHabit(ref, day, done)"></calendar-habits></div>
{/if}
{#if state.error}
<p class="nu-note"><ui-icon name="warning-circle" size="16"></ui-icon>{state.error}</p>
{:else if state.loading}
<ui-spinner size="sm"></ui-spinner>
{:else if state.empty}
<p class="nu-note"><ui-icon name="calendar-check" size="16"></ui-icon>Nothing on your calendar for the next 3 days.</p>
{:else}
{#if state.counts.length}
<div class="nu-counts">
{#each state.counts as count}
<a class="nu-count" key={count.key} href={count.href}><ui-icon name="hourglass-simple" size="12"></ui-icon>{count.text}</a>
{/each}
</div>
{/if}
{#each state.groups as group}
<div class="nu-day" key={group.day}>
<span class="nu-day-name" class:today={group.label === 'Today'}>{group.label}</span>
<span class="nu-day-date">{group.date}</span>
{#if !group.rows.length}<span class="nu-free">Free</span>{/if}
</div>
{#each group.rows as row}
<div class="nu-row" key={group.day + row.key} style="--tag: var(--color-avatar-{row.color})">
<a class="nu-open" href={row.href} title={row.tip}>
<span class="nu-time">{row.time}</span>
<span class="nu-text">
<span class="nu-title">{row.title}</span>
{#if row.soon}
<span class="nu-sub">
<span class="nu-soon">{row.soon}</span>
{#if row.location}<span class="nu-where">{row.location}</span>{/if}
</span>
{/if}
</span>
{#if row.countdown}<span class="nu-flag" title="Counting down to it"><ui-icon name="hourglass-simple" size="14"></ui-icon></span>{/if}
</a>
{#if row.join}
<span class="nu-join"><ui-button size="sm" variant="primary" icon="video-camera" label="Join" href={row.join} target="_blank" rel="noreferrer noopener"></ui-button></span>
{/if}
</div>
{/each}
{#if group.more}<a class="nu-more" href={group.moreHref}>{group.more} more</a>{/if}
{/each}
{/if}
</div>
{/if}
</section>`
};
}

Fez('mail-next-up', __fez_component_0);

export default __fez_component_0;
